import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sparkles, Palette, Eye, Brush, Heart, ListChecks, BadgeCheck, AlertTriangle,
} from "lucide-react";

// Section card with elegant header
function Section({ icon: Icon, title, hint, children, testid }) {
  return (
    <section className="bg-white border border-border rounded-sm" data-testid={testid}>
      <header className="flex items-start gap-3 p-4 md:p-5 border-b border-border">
        <span className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-primary" strokeWidth={1.5} />
        </span>
        <div>
          <h3 className="font-serif-heading text-lg md:text-xl leading-tight">{title}</h3>
          {hint && <p className="text-[11px] md:text-xs text-neutral-500 mt-0.5">{hint}</p>}
        </div>
      </header>
      <div className="p-4 md:p-5">{children}</div>
    </section>
  );
}

// Plain text field — opt-in for placeholder hint "Marca / colore / prodotto"
function ProductField({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="label-uppercase">{label}</Label>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || "Marca · prodotto · colore"}
        className="mt-1 rounded-sm h-11"
        data-testid={testid}
      />
    </div>
  );
}

function NotesField({ label, value, onChange, rows = 3, placeholder, testid }) {
  return (
    <div>
      <Label className="label-uppercase">{label}</Label>
      <Textarea
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 rounded-sm"
        data-testid={testid}
      />
    </div>
  );
}

export default function MakeupSheet({ client, onPatch }) {
  const m = client.makeup || {};
  const base     = m.base || {};
  const eyes     = m.eyes || {};
  const brows    = m.brows || {};
  const lashes   = m.lashes || {};
  const lips     = m.lips || {};
  const notes    = m.sheet_notes || {};
  const approval = m.approval || {};

  // Helper: patch a nested section (immutably merges into client.makeup)
  const patchSection = (section, key, value) => {
    onPatch("makeup", {
      ...m,
      [section]: { ...(m[section] || {}), [key]: value },
    });
  };

  return (
    <div className="space-y-6" data-testid="makeup-sheet">
      {/* BASE VISO */}
      <Section icon={Palette} title="Base viso" hint="Preparazione e incarnato" testid="makeup-section-base">
        <div className="grid sm:grid-cols-2 gap-4">
          <ProductField label="Primer" value={base.primer} onChange={(v) => patchSection("base", "primer", v)} testid="m-base-primer" />
          <ProductField label="Correttore 1" value={base.corrector_1} onChange={(v) => patchSection("base", "corrector_1", v)} testid="m-base-corr1" />
          <ProductField label="Correttore 2" value={base.corrector_2} onChange={(v) => patchSection("base", "corrector_2", v)} testid="m-base-corr2" />
          <ProductField label="Fondotinta" value={base.foundation} onChange={(v) => patchSection("base", "foundation", v)} testid="m-base-foundation" />
          <ProductField label="Cipria" value={base.powder} onChange={(v) => patchSection("base", "powder", v)} testid="m-base-powder" />
          <ProductField label="Contouring" value={base.contouring} onChange={(v) => patchSection("base", "contouring", v)} testid="m-base-contour" />
          <ProductField label="Blush" value={base.blush} onChange={(v) => patchSection("base", "blush", v)} testid="m-base-blush" />
          <ProductField label="Illuminante" value={base.highlighter} onChange={(v) => patchSection("base", "highlighter", v)} testid="m-base-highlighter" />
        </div>
      </Section>

      {/* OCCHI */}
      <Section icon={Eye} title="Occhi" hint="Costruzione del look" testid="makeup-section-eyes">
        <div className="grid sm:grid-cols-2 gap-4">
          <ProductField label="Primer occhi" value={eyes.primer} onChange={(v) => patchSection("eyes", "primer", v)} testid="m-eyes-primer" />
          <ProductField label="Colore base" value={eyes.base_color} onChange={(v) => patchSection("eyes", "base_color", v)} testid="m-eyes-base" />
          <ProductField label="Palpebra mobile" value={eyes.mobile_lid} onChange={(v) => patchSection("eyes", "mobile_lid", v)} testid="m-eyes-mobile" />
          <ProductField label="Palpebra fissa" value={eyes.fixed_lid} onChange={(v) => patchSection("eyes", "fixed_lid", v)} testid="m-eyes-fixed" />
          <ProductField label="Sfumatura" value={eyes.blending} onChange={(v) => patchSection("eyes", "blending", v)} testid="m-eyes-blend" />
          <ProductField label="Eyeliner" value={eyes.eyeliner} onChange={(v) => patchSection("eyes", "eyeliner", v)} testid="m-eyes-eyeliner" />
          <ProductField label="Mascara" value={eyes.mascara} onChange={(v) => patchSection("eyes", "mascara", v)} testid="m-eyes-mascara" />
          <ProductField label="Punto luce" value={eyes.inner_corner} onChange={(v) => patchSection("eyes", "inner_corner", v)} testid="m-eyes-corner" />
        </div>
        <div className="mt-4">
          <NotesField
            label="Tecnica"
            rows={2}
            value={eyes.technique}
            onChange={(v) => patchSection("eyes", "technique", v)}
            placeholder="Es. cut crease · smokey · halo · sfumato classico…"
            testid="m-eyes-technique"
          />
        </div>
      </Section>

      {/* SOPRACCIGLIA + CIGLIA */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Section icon={Brush} title="Sopracciglia" hint="Disegno e fissaggio" testid="makeup-section-brows">
          <div className="space-y-4">
            <ProductField label="Disegno" value={brows.drawing} onChange={(v) => patchSection("brows", "drawing", v)} testid="m-brows-drawing" />
            <ProductField label="Riempimento" value={brows.filling} onChange={(v) => patchSection("brows", "filling", v)} testid="m-brows-filling" />
            <ProductField label="Fissaggio" value={brows.fixing} onChange={(v) => patchSection("brows", "fixing", v)} testid="m-brows-fixing" />
          </div>
        </Section>

        <Section icon={Sparkles} title="Ciglia" hint="Applicazione e dettagli" testid="makeup-section-lashes">
          <label className="flex items-center gap-3 cursor-pointer p-3 -m-3 rounded-sm hover:bg-secondary/30">
            <Checkbox
              checked={!!lashes.applied}
              onCheckedChange={(c) => patchSection("lashes", "applied", !!c)}
              data-testid="m-lashes-applied"
            />
            <span className="font-medium">Applicazione ciglia finte</span>
          </label>

          {lashes.applied && (
            <div className="mt-4 space-y-4 border-t border-border pt-4">
              <div>
                <Label className="label-uppercase">Tipo ciglia</Label>
                <Select
                  value={lashes.type || ""}
                  onValueChange={(v) => patchSection("lashes", "type", v)}
                >
                  <SelectTrigger className="mt-1 rounded-sm h-11" data-testid="m-lashes-type">
                    <SelectValue placeholder="Seleziona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ciuffetti">Ciuffetti</SelectItem>
                    <SelectItem value="banda intera">Banda intera</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-uppercase">Lunghezza / effetto</Label>
                <Select
                  value={lashes.length_effect || ""}
                  onValueChange={(v) => patchSection("lashes", "length_effect", v)}
                >
                  <SelectTrigger className="mt-1 rounded-sm h-11" data-testid="m-lashes-effect">
                    <SelectValue placeholder="Seleziona effetto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="naturale">Naturale</SelectItem>
                    <SelectItem value="volume">Volume</SelectItem>
                    <SelectItem value="cat eye">Cat eye</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <ProductField label="Colla utilizzata" value={lashes.glue} onChange={(v) => patchSection("lashes", "glue", v)} testid="m-lashes-glue" />
              <NotesField label="Note applicazione" rows={2} value={lashes.notes} onChange={(v) => patchSection("lashes", "notes", v)} testid="m-lashes-notes" />
            </div>
          )}
        </Section>
      </div>

      {/* LABBRA */}
      <Section icon={Heart} title="Labbra" hint="Definizione e finitura" testid="makeup-section-lips">
        <div className="grid sm:grid-cols-2 gap-4">
          <ProductField label="Base labbra" value={lips.base} onChange={(v) => patchSection("lips", "base", v)} testid="m-lips-base" />
          <ProductField label="Matita" value={lips.pencil} onChange={(v) => patchSection("lips", "pencil", v)} testid="m-lips-pencil" />
          <ProductField label="Rossetto" value={lips.lipstick} onChange={(v) => patchSection("lips", "lipstick", v)} testid="m-lips-lipstick" />
          <ProductField label="Gloss" value={lips.gloss} onChange={(v) => patchSection("lips", "gloss", v)} testid="m-lips-gloss" />
          <div>
            <Label className="label-uppercase">Finitura</Label>
            <Select
              value={lips.finish || ""}
              onValueChange={(v) => patchSection("lips", "finish", v)}
            >
              <SelectTrigger className="mt-1 rounded-sm h-11" data-testid="m-lips-finish">
                <SelectValue placeholder="Seleziona finitura" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="matte">Matte</SelectItem>
                <SelectItem value="satin">Satin</SelectItem>
                <SelectItem value="gloss">Gloss</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      {/* NOTE */}
      <Section icon={ListChecks} title="Note & correzioni" hint="Annotazioni separate per area" testid="makeup-section-notes">
        <div className="grid md:grid-cols-2 gap-4">
          <NotesField
            label="Note trucco"
            rows={3}
            value={notes.general}
            onChange={(v) => patchSection("sheet_notes", "general", v)}
            placeholder="Indicazioni generali, riferimenti, mood…"
            testid="m-notes-general"
          />
          <NotesField
            label="Correzioni viso"
            rows={3}
            value={notes.face_corrections}
            onChange={(v) => patchSection("sheet_notes", "face_corrections", v)}
            placeholder="Es. coprire imperfezione, asimmetrie zigomi…"
            testid="m-notes-face"
          />
          <NotesField
            label="Correzioni occhi"
            rows={3}
            value={notes.eye_corrections}
            onChange={(v) => patchSection("sheet_notes", "eye_corrections", v)}
            placeholder="Es. occhio incappucciato, asimmetrie palpebre…"
            testid="m-notes-eyes"
          />
          <NotesField
            label="Correzioni labbra"
            rows={3}
            value={notes.lip_corrections}
            onChange={(v) => patchSection("sheet_notes", "lip_corrections", v)}
            placeholder="Es. ridefinire arco, riempire angoli…"
            testid="m-notes-lips"
          />
        </div>
      </Section>

      {/* APPROVAZIONE */}
      <Section icon={BadgeCheck} title="Approvazione cliente" hint="Esito prova trucco" testid="makeup-section-approval">
        <label
          className={`flex items-center gap-3 p-4 rounded-sm cursor-pointer transition-colors border ${
            approval.approved
              ? "bg-emerald-50 border-emerald-200"
              : "bg-secondary/30 border-border hover:border-primary/40"
          }`}
        >
          <Checkbox
            checked={!!approval.approved}
            onCheckedChange={(c) => patchSection("approval", "approved", !!c)}
            data-testid="m-approval-checkbox"
            className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
          />
          <div>
            <div className={`font-medium ${approval.approved ? "text-emerald-800" : "text-neutral-900"}`}>
              Trucco approvato
            </div>
            <div className="text-xs text-neutral-500">
              {approval.approved ? "La cliente ha confermato il look definitivo" : "Spunta dopo conferma cliente"}
            </div>
          </div>
        </label>

        {!approval.approved && (
          <div className="mt-4">
            <NotesField
              label={
                <span className="flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3" strokeWidth={2} /> Modifiche richieste
                </span>
              }
              rows={3}
              value={approval.requested_changes}
              onChange={(v) => patchSection("approval", "requested_changes", v)}
              placeholder="Annota cosa la cliente vuole modificare per il giorno dell'evento…"
              testid="m-approval-changes"
            />
          </div>
        )}
      </Section>
    </div>
  );
}
